/*
| The one place the explorer talks to Monaco's public REST API.
|
| Cross-origin and unauthenticated: the API host is per environment
| (VITE_API_URL), CORS is open for reads, and no cookie is ever sent.
|
| THE BUDGET. Analytics reads, the trade feed, the liquidation feed and the
| insurance funds share one per-IP public-read limiter (60 a minute, bursts of
| 120). The cache below is what keeps a reader clicking between screens inside
| it: every read is cached by URL for a short TTL, identical reads in flight
| share one request, and the cache survives a reload through sessionStorage.
| A 429 surfaces as a rate-limited ApiError carrying Retry-After, never a loop.
*/

import { routes } from './routes.js';

const DEFAULT_TTL_MS = 60_000;
/** The catalog changes when a market is listed, not between page views. */
export const CATALOG_TTL_MS = 5 * 60_000;
const STORAGE_PREFIX = 'explorer-ui:v1:';
const PAGE_SIZE = 100;

export class ApiError extends Error {
  constructor(status, path, body, { retryAfter = null } = {}) {
    const message = status === 429
      ? `Public read rate limit reached; retry in ${retryAfter}s`
      : status === 0
        ? `GET ${path} did not reach the API`
        : `GET ${path} failed with ${status}`;
    super(message);
    this.status = status;
    this.path = path;
    this.body = body;
    this.retryAfter = retryAfter;
  }

  /** A 404 is a real answer ("no such market"), not an outage. */
  get isNotFound() {
    return this.status === 404;
  }

  get isRateLimited() {
    return this.status === 429;
  }
}

/** Retry-After as whole seconds: delta-seconds or an HTTP date; a minute when unreadable. */
export function parseRetryAfter(header, nowMs = Date.now()) {
  if (header === null || header === undefined || header === '') return 60;
  if (/^\d+$/.test(String(header).trim())) return Number(header);
  const at = Date.parse(header);
  if (Number.isNaN(at)) return 60;
  return Math.max(0, Math.ceil((at - nowMs) / 1000));
}

function readStore(storage, key) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** The default cap on cached responses kept in sessionStorage. */
const MAX_STORED_ENTRIES = 200;

/** Drops this cache's expired entries, then the soonest-expiring beyond `max`. */
function pruneStore(storage, now, max) {
  const ours = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
    let expires = 0;
    try {
      expires = Number(JSON.parse(storage.getItem(key))?.expires) || 0;
    } catch {
      // Unreadable: treat as expired.
    }
    ours.push({ key, expires });
  }

  const live = ours.filter((entry) => entry.expires > now);
  const doomed = ours.filter((entry) => entry.expires <= now);
  live.sort((a, b) => a.expires - b.expires);
  doomed.push(...live.slice(0, Math.max(0, live.length - max)));
  doomed.forEach(({ key }) => storage.removeItem(key));
}

function countOurs(storage) {
  let count = 0;
  for (let i = 0; i < storage.length; i += 1) {
    if (storage.key(i)?.startsWith(STORAGE_PREFIX)) count += 1;
  }
  return count;
}

function writeStore(storage, key, entry, now, max) {
  if (!storage) return;
  const write = () => storage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  try {
    write();
  } catch {
    // Over quota: halve what this cache actually holds, then retry once;
    // otherwise memory still serves.
    try {
      pruneStore(storage, now, Math.floor(countOurs(storage) / 2));
      write();
    } catch {
      return;
    }
  }
  try {
    if (typeof storage.key === 'function') pruneStore(storage, now, max);
  } catch {
    // Pruning is best effort.
  }
}

/** Window bounds floored to the minute, so repeat reads share one cache entry. */
const MINUTE = 60_000;
const floorMinute = (ms) => (ms === undefined || ms === null ? ms : Math.floor(Number(ms) / MINUTE) * MINUTE);

/**
 * @param {{ baseUrl: string, fetchImpl?: typeof fetch, now?: () => number,
 *           storage?: Pick<Storage, 'getItem'|'setItem'>|null, ttlMs?: number }} options
 */
export function createApi({ baseUrl, fetchImpl = globalThis.fetch, now = Date.now, storage = null, ttlMs = DEFAULT_TTL_MS, maxStoredEntries = MAX_STORED_ENTRIES }) {
  const base = String(baseUrl).replace(/\/+$/, '');
  const cache = new Map();
  const inflight = new Map();

  async function fetchJson(path) {
    let response;
    try {
      response = await fetchImpl(`${base}${path}`, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      });
    } catch (cause) {
      throw new ApiError(0, path, { message: cause?.message ?? 'network error' });
    }

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const retryAfter = response.status === 429
        ? parseRetryAfter(response.headers?.get?.('retry-after'), now())
        : null;
      throw new ApiError(response.status, path, body, { retryAfter });
    }

    return body;
  }

  /** A cached GET of `path` (a routes.* result). */
  async function get(path, { ttl = ttlMs } = {}) {
    const fresh = (entry) => entry && entry.expires > now();

    const memory = cache.get(path);
    if (fresh(memory)) return memory.value;

    const stored = readStore(storage, path);
    if (fresh(stored)) {
      cache.set(path, stored);
      return stored.value;
    }

    if (inflight.has(path)) return inflight.get(path);

    const request = fetchJson(path)
      .then((value) => {
        const entry = { value, expires: now() + ttl };
        cache.set(path, entry);
        writeStore(storage, path, entry, now(), maxStoredEntries);
        return value;
      })
      .finally(() => inflight.delete(path));

    inflight.set(path, request);
    return request;
  }

  /** Every page of a page-numbered listing. */
  async function allPages(route, itemsKey, ttl) {
    const items = [];
    for (let page = 1; ; page += 1) {
      const body = await get(route(page), { ttl });
      items.push(...(body?.[itemsKey] ?? []));
      const totalPages = Number(body?.totalPages ?? 1);
      if (page >= totalPages || page >= 50) return items;
    }
  }

  return {
    get,

    /** The active market catalog: symbols, modes, categories, decimals and limits. */
    allTradingPairs: () =>
      allPages((page) => routes.market.pairs({ page, pageSize: PAGE_SIZE, isActive: true }), 'tradingPairs', CATALOG_TTL_MS),

    /** Prices, 24h and lifetime volume, open interest and funding for every active market. */
    allScreenerItems: () => allPages((page) => routes.market.screener({ page, pageSize: PAGE_SIZE }), 'items'),

    marketMetadata: (id) => get(routes.market.metadata(id)),
    perpSummary: (id) => get(routes.market.perpSummary(id)),

    async candles(id, interval, { startTime, endTime, limit } = {}) {
      const body = await get(routes.market.candles(id, interval, { startTime: floorMinute(startTime), endTime: floorMinute(endTime), limit }));
      return body?.data ?? body?.candles ?? [];
    },

    async pairTrades(id, { pageSize = 25 } = {}) {
      const body = await get(routes.trades.byPair(id, { pageSize }), { ttl: 10_000 });
      return body?.trades ?? [];
    },

    /** A pair's fee tiers (unmetered). Changes when the schedule does, so it caches like the catalog. */
    feeSchedule: (id) => get(routes.fees.schedule({ tradingPairId: id }), { ttl: CATALOG_TTL_MS }),

    liquidationMetrics: (id, { interval, startTime, endTime }) =>
      get(routes.market.liquidationMetrics(id, { interval, startTime: floorMinute(startTime), endTime: floorMinute(endTime) })),

    /** Per-market volume, protocol fees and trades over a window (metered). */
    async marketVolumes({ window = '24h', limit = 100 } = {}) {
      const body = await get(routes.analytics.marketVolumes({ window, limit }));
      return body?.markets ?? [];
    },

    /** Protocol totals over a window (`{ window, startTime?, endTime? }`). */
    protocolTotals: (params) => get(routes.analytics.protocolTotals(params)),

    async protocolSeries(params) {
      const body = await get(routes.analytics.protocolSeries(params));
      return { ...body, points: body?.points ?? [] };
    },

    treasuryNetFees: () => get(routes.analytics.treasuryNetFees()),

    /** Every displayed builder. Hidden ones are never sent. */
    async builders() {
      const body = await get(routes.analytics.builders(), { ttl: CATALOG_TTL_MS });
      return body?.builders ?? [];
    },

    /** One builder by uuid, code or client id; a hidden one is a 404. */
    async builder(key) {
      const body = await get(routes.analytics.builder(key));
      return body?.builder ?? null;
    },

    builderTotals: (key, params) => get(routes.analytics.builderTotals(key, params)),

    async builderSeries(key, params) {
      const body = await get(routes.analytics.builderSeries(key, params));
      return { ...body, points: body?.points ?? [] };
    },

    async builderLeaderboard(params) {
      const body = await get(routes.analytics.builderLeaderboard({ limit: 100, ...params }));
      return { ...body, rows: body?.rows ?? [] };
    },

    /** Custody per token and TVL (USDC only). */
    async tokenCustody() {
      const body = await get(routes.analytics.tokens());
      return { ...body, tokens: body?.tokens ?? [] };
    },

    /** One token's custody by symbol, asset uuid or contract; unknown is a 404. */
    async tokenCustodyOf(token) {
      const body = await get(routes.analytics.token(token));
      return body?.token ?? null;
    },

    /** Wallets by all-time taker volume, up to 100. */
    async walletLeaderboard({ limit = 100 } = {}) {
      const body = await get(routes.analytics.walletLeaderboard({ limit }));
      return body?.wallets ?? [];
    },

    walletSummary: (wallet) => get(routes.analytics.wallet(wallet)),

    async walletLiquidations(wallet, { pageSize = 50, pageToken } = {}) {
      const body = await get(routes.analytics.walletLiquidations(wallet, { pageSize, pageToken }));
      return { events: body?.events ?? [], nextPageToken: body?.nextPageToken ?? '' };
    },

    /** Deposits, newest first; `token`/`wallet` filter once the API supports them (0XM-3663). */
    async deposits({ pageSize = 25, pageToken, token, wallet } = {}) {
      const body = await get(routes.analytics.deposits({ pageSize, pageToken, token, wallet }), { ttl: 15_000 });
      return { rows: body?.deposits ?? [], nextPageToken: body?.nextPageToken ?? '' };
    },

    async withdrawals({ pageSize = 25, pageToken, token, wallet } = {}) {
      const body = await get(routes.analytics.withdrawals({ pageSize, pageToken, token, wallet }), { ttl: 15_000 });
      return { rows: body?.withdrawals ?? [], nextPageToken: body?.nextPageToken ?? '' };
    },

    async insuranceFunds() {
      const body = await get(routes.market.insuranceFunds());
      return body?.funds ?? [];
    },

    /** The trade feed, newest first, optionally one application's taker trades. */
    async tradeFeed({ takerApplicationId, pageSize = 20 } = {}) {
      const body = await get(routes.trades.feed({ takerApplicationId, pageSize }), { ttl: 10_000 });
      return body?.trades ?? [];
    },

    /**
     * Liquidation events since `startTime`, newest first, following the cursor
     * for at most `maxPages` pages (each one metered). `truncated` says the
     * window holds more than was read.
     */
    async recentLiquidations({ startTime, pageSize = 1000, maxPages = 3 } = {}) {
      const events = [];
      let pageToken;
      for (let page = 0; page < maxPages; page += 1) {
        const body = await get(routes.market.liquidationEvents({ startTime: floorMinute(startTime), pageSize, pageToken }));
        events.push(...(body?.events ?? []));
        pageToken = body?.nextPageToken ?? '';
        if (!pageToken) return { events, truncated: false };
      }
      return { events, truncated: true };
    },
  };
}
